import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../lib/i18n'

/**
 * "Sou humano": arraste o ponteiro até sintonizar a faixa marcada, num dial
 * de 88–108 (a cara de um dial de FM, reconhecível mesmo por quem não é
 * radioamador).
 *
 * Reaproveita a metáfora do próprio app em vez de um CAPTCHA de terceiro —
 * sem serviço externo, sem segredo novo pra configurar. Não é à prova de um
 * atacante que leia a resposta de /api/auth/desafio e devolva o alvo exato
 * por HTTP puro (o alvo PRECISA ser visível pro olho ver onde mirar, esse
 * caminho sempre existe). O que isto barra é preenchimento automatizado
 * ingênuo — e um agente que opera por clique/tecla discreto, não por
 * arrasto contínuo de mouse.
 *
 * Em desenvolvimento (RADIOAMADOR_DEV_AUTH=1) o servidor responde
 * `{ desabilitado: true }` e este componente não renderiza nada.
 */

interface Desafio {
  token: string
  alvo: number
  min: number
  max: number
  tolerancia: number
}

export function HumanCheck({
  onChange,
}: {
  onChange: (provado: boolean, prova: { token: string; valor: number } | null) => void
}) {
  const { t } = useI18n()
  const [desafio, setDesafio] = useState<Desafio | 'desabilitado' | 'carregando' | 'erro'>(
    'carregando',
  )
  const [valor, setValor] = useState(0)
  const [travado, setTravado] = useState(false)
  const trilhaRef = useRef<HTMLDivElement>(null)
  const arrastandoRef = useRef(false)

  useEffect(() => {
    let cancelado = false
    fetch('/api/auth/desafio')
      .then((r) => r.json())
      .then((d: Partial<Desafio> & { desabilitado?: boolean }) => {
        if (cancelado) return
        if (d.desabilitado) {
          setDesafio('desabilitado')
          onChange(true, null)
          return
        }
        if (
          typeof d.token === 'string' &&
          typeof d.alvo === 'number' &&
          typeof d.min === 'number' &&
          typeof d.max === 'number' &&
          typeof d.tolerancia === 'number'
        ) {
          setDesafio(d as Desafio)
          setValor(d.min + (d.max - d.min) / 2)
        } else {
          setDesafio('erro')
        }
      })
      .catch(() => {
        if (!cancelado) setDesafio('erro')
      })
    return () => {
      cancelado = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (desafio === 'desabilitado') return null
  if (desafio === 'carregando') return <p className="desafio-legenda">{t.login.desafioCarregando}</p>
  if (desafio === 'erro') return <p className="denial">{t.login.erroDesafio}</p>

  const d = desafio

  const valorNaPosicao = (clientX: number): number => {
    const el = trilhaRef.current
    if (!el) return valor
    const rect = el.getBoundingClientRect()
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    return d.min + frac * (d.max - d.min)
  }

  const mover = (v: number) => {
    setValor(v)
    if (Math.abs(v - d.alvo) <= d.tolerancia) {
      setTravado(true)
      onChange(true, { token: d.token, valor: v })
    } else if (travado) {
      setTravado(false)
      onChange(false, null)
    }
  }

  const pointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    arrastandoRef.current = true
    mover(valorNaPosicao(e.clientX))
  }
  const pointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!arrastandoRef.current) return
    mover(valorNaPosicao(e.clientX))
  }
  const pointerUp = () => {
    arrastandoRef.current = false
  }

  const pct = (v: number) => ((v - d.min) / (d.max - d.min)) * 100

  return (
    <div className={`desafio${travado ? ' ok' : ''}`}>
      <label>{t.login.desafioInstrucao}</label>
      <div
        className="desafio-trilha"
        ref={trilhaRef}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
      >
        <div
          className="desafio-alvo"
          style={{
            left: `${pct(d.alvo - d.tolerancia)}%`,
            width: `${pct(d.alvo + d.tolerancia) - pct(d.alvo - d.tolerancia)}%`,
          }}
        />
        <div className="desafio-agulha" style={{ left: `${pct(valor)}%` }} />
      </div>
      <div className="desafio-legenda">
        <span>{d.min.toFixed(1)}</span>
        <strong>{valor.toFixed(1)}</strong>
        <span>{d.max.toFixed(1)}</span>
      </div>
      {travado && <p className="desafio-ok">{t.login.desafioSintonizado}</p>}
    </div>
  )
}
